import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { emptyArray } from 'ui-components/default-props/empty';

import { BatchesListPageActions } from './batches-list-page-actions';
import BatchesListPageHeader from './batches-list-page-header';
import { OPEN, PROCESSED, ADVANCED_SEARCH, ARCHIVED } from './constants';
import BatchesListPlaceholder from '../placeholder';
import BatchGridContainer from './containers/batch-grid-container';
import GridPagingContainer from './containers/grid-paging-container';
import BatchesListFilterBar from './filter-bar';
import { openOptions, processedOptions, archivedOptions, getDefaultFiltersForTab } from './filter-bar/filter-options';
import style from './style.module.less';
import GridWithPaging from '../../../components/page-layout/grid-with-paging';
import { FiltersContext } from '../../../filters/context';
import { batchRowsGetReplay } from '../../../store/batches/batches.grid.observables';
import { getBatchRows } from '../../../store/batches/grid/batches.grid.actions';
import Global from '../../../store/global-selectors';

const matchPropType = PropTypes.shape({
  params: PropTypes.shape({
    tabFilter: PropTypes.string,
  }),
});

const selectAllFilterBys = (selectedTab) => {
  if (selectedTab === OPEN) {
    return openOptions;
  }
  if (selectedTab === PROCESSED) {
    return processedOptions;
  }
  if (selectedTab === ARCHIVED) {
    return archivedOptions;
  }
  return emptyArray;
};

const BatchesListPage = ({ batchId = '', match, isLoading = true, selectedTab, showPlaceholder = false }) => {
  const dispatch = useDispatch();
  const { buildApiFilters } = useContext(FiltersContext);
  const currentUserId = useSelector(Global.selectUserCurrentUserId);

  const [defaultFilters, setDefaultFilters] = useState(getDefaultFiltersForTab(selectedTab, { currentUserId }));
  const [allFilterBys, setAllFilterBys] = useState(selectAllFilterBys(selectedTab));

  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    // update passed filterbar data whenever tab changes. Filterbar shouldn't be tab-aware.
    if (!batchId) {
      setDefaultFilters(getDefaultFiltersForTab(selectedTab, { currentUserId }));
      setAllFilterBys(selectAllFilterBys(selectedTab));
    }
  }, [batchId, currentUserId, selectedTab]);

  useEffect(() => {
    batchRowsGetReplay.next(buildApiFilters());
    return () => batchRowsGetReplay.next();
  }, [buildApiFilters]);

  const isMounted2 = useRef(false);
  useEffect(() => {
    if (!isMounted2.current) {
      isMounted2.current = true;
      return;
    }
    if (!batchId) {
      dispatch(getBatchRows());
    }
  }, [buildApiFilters, dispatch, batchId]);

  return (
    <div className={style['batches-list-page']}>
      <GridWithPaging>
        <BatchesListPageHeader selectedTab={selectedTab} />
        <BatchesListPageActions />
        <BatchesListFilterBar
          allFilterBys={allFilterBys}
          defaultFilters={defaultFilters}
          isHidden={selectedTab === ADVANCED_SEARCH}
          selectedTab={selectedTab}
        />

        {isLoading ? <LoadingIndicator /> : null}
        {showPlaceholder ? <BatchesListPlaceholder /> : null}
        {!showPlaceholder && !isLoading && (
          <>
            <div className={style.grid}>
              <BatchGridContainer match={match} />
            </div>
            <div className={[style['paging-footer']]}>
              <GridPagingContainer />
            </div>
          </>
        )}
      </GridWithPaging>
    </div>
  );
};

BatchesListPage.propTypes = {
  isLoading: PropTypes.bool,
  selectedTab: PropTypes.oneOf([OPEN, PROCESSED, ARCHIVED, ADVANCED_SEARCH]).isRequired,
  showPlaceholder: PropTypes.bool,
  match: matchPropType.isRequired,
  batchId: PropTypes.string,
};

export default BatchesListPage;
